import { ReactNode, useState, useEffect } from "react";
import { AffiliateContext, AffiliateContextType } from "./context";
import { affiliateService } from "../../services/affiliate.service";
import {
  AffiliateDetails,
  UpdateAffiliateDetails,
} from "../../types/affiliate";

interface AffiliateProviderProps {
  children: ReactNode;
}

export function AffiliateProvider({ children }: AffiliateProviderProps) {
  const [details, setDetails] = useState<AffiliateDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetchAffiliate = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await affiliateService.getDetails();
      setDetails(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch affiliate data"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const updateDetails = async (data: UpdateAffiliateDetails) => {
    try {
      setIsLoading(true);
      setError(null);
      const updated = await affiliateService.updateDetails(data);
      setDetails(updated);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update affiliate data"
      );
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch affiliate details on mount
  useEffect(() => {
    refetchAffiliate();
  }, []);

  const value: AffiliateContextType = {
    details,
    isLoading,
    error,
    refetchAffiliate,
    updateDetails,
  };

  return (
    <AffiliateContext.Provider value={value}>
      {children}
    </AffiliateContext.Provider>
  );
}
